/**
 * 将 maleSpin 接口数据转为页面现有展示结构（转盘格子、任务行、榜单等）
 */

/** @typedef {{ taskId: string, rewardTicket: number, rewardPoints: number, progressCount: number, targetCount: number, completed: boolean, rewardGranted?: boolean }} ApiTask */

const TASK_META = {
  D_MATCH: { titleKey: 'lang.MayDay2026.k013', nums: [20], action: 'open_match_page' },
  D_PRIVATE: { titleKey: 'lang.MayDay2026.k010', nums: [], action: 'open_session_page' },
  S_MATCH: { titleKey: 'lang.MayDay2026.k011', nums: [20], action: 'open_match_page' },
  S_PRIVATE: { titleKey: 'lang.MayDay2026.k012', nums: [60], action: 'open_session_page' },
  R_MATCH_60: { titleKey: 'lang.MayDay2026.k013', nums: [60], action: 'open_match_page' },
  R_PRIVATE_60: { titleKey: 'lang.MayDay2026.k014', nums: [60], action: 'open_session_page' },
  R_MATCH_120: { titleKey: 'lang.MayDay2026.k013', nums: [120], action: 'open_match_page' },
  R_PRIVATE_120: { titleKey: 'lang.MayDay2026.k014', nums: [120], action: 'open_session_page' },
}

function buildRewards(task) {
  const list = []
  const t = Number(task.rewardTicket) || 0
  const p = Number(task.rewardPoints) || 0
  if (t > 0) list.push({ kind: 'ticket', value: t })
  if (p > 0) list.push({ kind: 'coin', value: p })
  return list
}

/**
 * @param {ApiTask[]} list
 * @param {(key: string) => string} t
 * @param {'daily'|'streak'|'repeatable'} mode
 */
export function mapApiTasksToRows(list, t, mode) {
  if (!Array.isArray(list)) return []
  return list.map((task) => {
    const meta = TASK_META[task.taskId] || {
      titleKey: 'lang.MayDay2026.k013',
      nums: [20],
      action: 'open_match_page',
    }
    const row = {
      id: task.taskId,
      taskId: task.taskId,
      title: t(meta.titleKey),
      numsOption: meta.nums.map((v) => ({ value: v })),
      proc: String(task.progressCount ?? 0),
      target: String(task.targetCount ?? 1),
      rewards: buildRewards(task),
      action: meta.action,
    }
    if (mode === 'repeatable') {
      row.state = 0
      return row
    }
    // daily / streak：未完成可跳转；已完成展示倒计时区
    row.state = task.completed ? 1 : 0
    return row
  })
}

/**
 * Silver 转盘：rewardType !== 0 与 rewardType === 0 两组按索引交叉（每项先非 0 再 0，较长一侧剩余接在后）
 * @param {Array<{ rewardId?: number, rewardType?: number, rewardValue?: number }>} list
 */
export function interleaveSilverPoolRewards(list) {
  if (!Array.isArray(list) || !list.length) return []
  const nonZero = list.filter((r) => Number(r.rewardType) !== 0)
  const zero = list.filter((r) => Number(r.rewardType) === 0)
  const out = []
  const n = Math.max(nonZero.length, zero.length)
  for (let k = 0; k < n; k++) {
    if (k < nonZero.length) out.push(nonZero[k])
    if (k < zero.length) out.push(zero[k])
  }
  return out
}

/**
 * drawOptions.silver / .gold 内的 rewards → 转盘 panData（id / propType / count）
 * @param {Array<{ rewardId?: number, rewardType?: number, rewardValue?: number }>} list
 */
export function mapDrawOptionRewardsToPan(list) {
  if (!Array.isArray(list) || !list.length) return []
  return list.map((r) => ({
    id: r.rewardId,
    propType: Number(r.rewardType),
    count: Number(r.rewardValue),
  }))
}

/** Silver drawOptions.rewards：先交叉排序再映射为转盘格子 */
export function mapSilverDrawOptionRewardsToPan(rewards) {
  return mapDrawOptionRewardsToPan(interleaveSilverPoolRewards(rewards))
}

/**
 * @param {object} raw maleSpin /home 原始 data
 * @param {{ silver?: object[], gold?: object[] }} [wheelConfig] 可选：当接口未返回 silverWheelRewards / goldWheelRewards 时的回退格子数据
 */
export function normalizeMaleSpinHome(raw, wheelConfig) {
  if (!raw || raw.error) return null
  const draw = raw.drawOptions || {}
  const silverOpt = draw.silver || {}
  const goldOpt = draw.gold || {}
  const silverFromDraw = mapSilverDrawOptionRewardsToPan(silverOpt.rewards)
  const goldFromDraw = mapDrawOptionRewardsToPan(goldOpt.rewards)
  const silverRewards = silverFromDraw.length
    ? silverFromDraw
    : Array.isArray(raw.silverWheelRewards) && raw.silverWheelRewards.length
    ? raw.silverWheelRewards
    : (wheelConfig && wheelConfig.silver) || []
  const goldRewards = goldFromDraw.length
    ? goldFromDraw
    : Array.isArray(raw.goldWheelRewards) && raw.goldWheelRewards.length
    ? raw.goldWheelRewards
    : (wheelConfig && wheelConfig.gold) || []

  const pool = String(raw.defaultPool || 'SILVER').toUpperCase()
  const defaultPoolKey = pool === 'GOLD' ? 'gold' : 'silver'

  return {
    ...raw,
    drawOptions: draw,
    endTimestamp: raw.eventEndTimestamp,
    startTimestamp: raw.eventStartTimestamp,
    countdown: raw.eventCountdown,
    score: raw.pointsBalance,
    rank: raw.currentRank,
    ticketBalance: raw.ticketBalance,
    eventStatus: raw.eventStatus,
    rankStatus: raw.rankStatus,
    streakDayCountdown: raw.streakDayCountdown,
    hasPrizeWheelRecord: raw.hasPrizeWheelRecord !== false,
    defaultPool: defaultPoolKey,
    topRewardPoints: raw.topRewardPoints,
    normalPrizeWheel: {
      restSpins: Number(silverOpt.availableSpins) || 0,
      costPoint: Number(silverOpt.cost) || 1,
      canSpinX1: silverOpt.canSpinX1 !== false,
      canSpinX10: silverOpt.canSpinX10 !== false,
      rewards: silverRewards,
    },
    advancedPrizeWheel: {
      restSpins: Number(goldOpt.availableSpins) || 0,
      costPoint: Number(goldOpt.cost) || 10,
      canSpinX1: goldOpt.canSpinX1 !== false,
      canSpinX10: goldOpt.canSpinX10 !== false,
      rewards: goldRewards,
    },
  }
}

/** 抽奖结果 → 转盘弹窗用结构 */
export function mapSpinResultsToPanRewards(results) {
  if (!Array.isArray(results)) return []
  return results.map((r, i) => ({
    id: `spin-${i}-${r.rewardType}-${r.rewardValue}`,
    propType: Number(r.rewardType),
    count: Number(r.rewardValue),
  }))
}

/** 单抽时根据结果匹配转盘扇区 id，用于指针动画 */
export function matchWheelSliceId(panData, result) {
  if (!result || !Array.isArray(panData)) return null
  const pt = Number(result.rewardType)
  const val = Number(result.rewardValue)
  const idx = panData.findIndex((c) => Number(c.propType) === pt && Number(c.count) === val)
  if (idx > -1 && panData[idx].id != null) return panData[idx].id
  const idxLoose = panData.findIndex((c) => Number(c.propType) === pt)
  if (idxLoose > -1 && panData[idxLoose].id != null) return panData[idxLoose].id
  return panData[0] && panData[0].id != null ? panData[0].id : null
}

/**
 * /leaderboard → actMaleRank 原 normalActRank 结构
 */
export function leaderboardApiToRankPayload(apiData) {
  if (!apiData || apiData.error) {
    return null
  }
  const top = Array.isArray(apiData.topList) ? apiData.topList : []
  const ranks = top.map((row) => ({
    nickname: row.nickname,
    avatar: row.avatar,
    rank: row.rank,
    score: row.points,
    uid: row.uid,
  }))
  const u = apiData.userInfo || {}
  const gapRaw = u.gapToNextRank != null ? u.gapToNextRank : apiData.gapToNextRank
  let gapToNextRank = null
  if (gapRaw != null && gapRaw !== '') {
    const gn = Number(gapRaw)
    gapToNextRank = Number.isFinite(gn) ? gn : null
  }
  const owners = {
    nickname: u.nickname,
    avatar: u.avatar,
    rank: u.rank,
    score: u.points,
    rankStatus: u.rankStatus,
    gapToNextRank,
  }
  return {
    ended: !!apiData.eventEnded,
    ranks,
    owners,
  }
}

/**
 * 抽奖历史单条 → 记录页结构
 */
export function mapHistoryRecord(rec) {
  if (!rec) return null
  const rewards = mapSpinResultsToPanRewards(rec.results || []).map((r) => ({
    propType: r.propType,
    count: r.count,
  }))
  return {
    time: rec.drawTimeUtc,
    poolType: rec.poolType,
    spinCount: rec.spinCount,
    rewards,
  }
}
